import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { ApiError, hasLiveApi, hasLivePlayerMfa } from '@/api/client'
import {
  getPlayersWithSameDeviceIds,
  toMultipleDeviceResult,
} from '@/api/map/memberDevice'
import { DEMO_MULTIPLE_DEVICES } from './multipleDeviceDemo'
import type {
  MultipleDeviceGroup,
  MultipleDeviceQuery,
  MultipleDeviceResult,
} from './multipleDeviceTypes'

export function filterMultipleDevices(
  rows: readonly MultipleDeviceGroup[],
  query: MultipleDeviceQuery = {},
): MultipleDeviceGroup[] {
  const deviceNeedle = query.deviceId?.trim().toLowerCase() ?? ''
  const playerNeedle = query.player?.trim().toLowerCase() ?? ''
  return rows.filter((row) => {
    if (deviceNeedle && !row.deviceId.toLowerCase().includes(deviceNeedle)) return false
    if (
      playerNeedle &&
      !row.players.some((p) => p.username.toLowerCase().includes(playerNeedle))
    ) {
      return false
    }
    return true
  })
}

export function paginateMultipleDevices(
  rows: readonly MultipleDeviceGroup[],
  query: MultipleDeviceQuery = {},
): MultipleDeviceResult {
  const filtered = filterMultipleDevices(rows, query)
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  const total = filtered.length
  const maxPage = Math.max(1, Math.ceil(total / pageSize) || 1)
  const page = Math.min(Math.max(1, query.page ?? 1), maxPage)
  const start = (page - 1) * pageSize
  return {
    rows: filtered.slice(start, start + pageSize),
    total,
    page,
    pageSize,
  }
}

/** Demo / offline fixtures — never returned under a live Keycloak session. */
export function listDemoMultipleDevices(query: MultipleDeviceQuery = {}): MultipleDeviceResult {
  return paginateMultipleDevices(DEMO_MULTIPLE_DEVICES, query)
}

/** Live empty when Player-API member-device admin route is missing / unreachable. */
export function unavailableMultipleDevices(
  query: MultipleDeviceQuery = {},
  reason?: string,
): MultipleDeviceResult {
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  return {
    rows: [],
    total: 0,
    page: 1,
    pageSize,
    unavailable: {
      code: 'no_member_device_source',
      reason:
        reason ??
        'Shared device-id listings need Player-API member-device admin. None is reachable, so this console will not invent device groups, cheater flags, or player chips.',
      trackedBy: 'backend-ui-logs:multiple-device-ids',
    },
  }
}

/**
 * Live Keycloak + Player-API → `getPlayersWithSameDeviceIds` (device_ids from
 * saveDeviceId: web fingerprint / native Android·iOS). Demo → fixtures only.
 */
export async function loadMultipleDevices(
  query: MultipleDeviceQuery = {},
): Promise<MultipleDeviceResult> {
  if (!hasLiveApi()) return listDemoMultipleDevices(query)
  if (!hasLivePlayerMfa()) {
    return unavailableMultipleDevices(
      query,
      'Player-API base URL is not configured for this console session.',
    )
  }

  try {
    const live = await getPlayersWithSameDeviceIds({
      page: query.page ?? 1,
      pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
      deviceId: query.deviceId,
      player: query.player,
    })
    if (!live.value) {
      return unavailableMultipleDevices(query, 'Member-device list returned no data.')
    }
    return toMultipleDeviceResult(live)
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0
    console.warn('[multiple-device-ids] Player-API member-device list unavailable', err)
    return unavailableMultipleDevices(
      query,
      status === 404
        ? 'Player-API member-device route is not deployed yet.'
        : status === 503
          ? 'Player-API admin key is not configured (ADMIN_API_KEY / Vite PLAYER_ADMIN_API_KEY).'
          : undefined,
    )
  }
}
